// Keep oversized camera photos below nutrition-photo-analyze's 2 MiB
// decoded-image limit. Work in memory; never upload or persist the original.
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_SIDE = 1600;

export async function prepareNutritionPhoto(base64: string): Promise<string> {
  if (Math.floor(base64.length * 0.75) <= MAX_IMAGE_BYTES) return base64;

  const image = new Image();
  const canvas = document.createElement("canvas");
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Photo could not be decoded"));
      image.src = `data:image/jpeg;base64,${base64}`;
    });
    if (!image.naturalWidth || !image.naturalHeight) {
      throw new Error("Photo has no usable dimensions");
    }

    const context = canvas.getContext("2d");
    if (!context) throw new Error("Photo processing is unavailable");
    const fit = Math.min(1, MAX_SIDE / image.naturalWidth, MAX_SIDE / image.naturalHeight);
    for (const [scale, quality] of [[1, 0.82], [1, 0.64], [0.5, 0.64]]) {
      canvas.width = Math.max(1, Math.round(image.naturalWidth * fit * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * fit * scale));
      context.fillStyle = "#fff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      if (!dataUrl.startsWith("data:image/jpeg;base64,")) {
        throw new Error("Photo encoding is unavailable");
      }
      const prepared = dataUrl.slice(dataUrl.indexOf(",") + 1);
      if (prepared && Math.floor(prepared.length * 0.75) <= MAX_IMAGE_BYTES) return prepared;
    }
    throw new Error("Photo could not be prepared for analysis");
  } finally {
    image.onload = null;
    image.onerror = null;
    image.removeAttribute("src");
    canvas.width = 1;
    canvas.height = 1;
  }
}
